import fetch from 'unfetch';
import { emit, hydrate } from '@/neon/events';

import { dispatchCustomEvent, CustomEvents } from '@/lib/dispatch-custom-event';

const ls = {
  get: () => JSON.parse(localStorage.getItem('cartItemOrder')),
  set: val => localStorage.setItem('cartItemOrder', val),
};

export function addVariant(variant, quantity) {
  const numAvailable =
    variant.inventory_policy === 'deny' &&
    variant.inventory_management === 'shopify'
      ? variant.inventory_quantity
      : null; // null means they can add as many as they want

  return fetchCart().then(({ items }) => {
    const existing = items.filter(item => item.id === variant.id)[0] || {};
    const numRequested = (existing.quantity || 0) + quantity;

    if (numAvailable !== null && numRequested > numAvailable) {
      const err = `There are only ${numAvailable} of that product available, requested ${numRequested}.`;
      emit('error', err);
      throw new Error(err);
    } else {
      return addItemById(variant.id, quantity);
    }
  });
}

export function updateAddon(id, quantity) {
  return fetchCart().then(({ items }) => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].variant_id === parseInt(id)) {
        return changeAddon(i + 1, quantity); // shopify cart is a 1-based index
      }
    }
  });
}

export function updateItem(id, quantity) {
  return get().then(({ items }) => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].variant_id === parseInt(id)) {
        return changeItem(i + 1, quantity); // shopify cart is a 1-based index
      }
    }
  });
}

export function removeAddon(id) {
  if (localStorageAvailable() && ls.get()) {
    removeItemFromLocalOrder(id);
  }

  return updateAddon(id, 0);
}

function changeAddon(line, quantity) {
  emit('cart:updating');

  return fetch(`${theme.routes.cart.change}.js`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ line, quantity }),
  })
    .then(res => res.json())
    .then(cart => {
      if (localStorageAvailable() && ls.get()) {
        cart.items = reorderCartItems(cart.items, ls.get());
      }

      hydrate({ cart: cart });
      emit('cart:updated', { cart: cart });
      dispatchCustomEvent(CustomEvents.cartUpdated, { cart: cart });
      return cart;
    });
}

/**
 * Warning: this does not check available products first
 */
export function addItemById(id, quantity, properties = {}) {
  emit('cart:updating');

  return fetch(`${theme.routes.cart.add}.js`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, quantity, properties }),
  })
    .then(r => r.json())
    .then(item => {
      return fetchCart(id).then(cart => {
        hydrate({ cart: cart });
        emit('cart:updated');

        return { item, cart };
      });
    });
}

export function getCart() {
  return fetch(`${theme.routes.cart.base}.js`, {
    method: 'GET',
    credentials: 'include',
  })
    .then(res => res.json())
    .then(data => {
      if (localStorageAvailable() && ls.get()) {
        data.items = reorderCartItems(data.items, ls.get());
      }

      return data;
    });
}

export function addItem(form) {
  emit('cart:updating');

  return fetch(`${theme.routes.cart.add}.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: serialize(form),
  })
    .then(r => r.json())
    .then(item => {
      if (item.status == '422') {
        const errorMessage = { code: 422, message: item.description };
        dispatchCustomEvent(CustomEvents.cartError, {
          errorMessage: item.description,
        });
        throw errorMessage;
      }

      return fetchCart(item.id).then(cart => {
        hydrate({ cart: cart });
        dispatchCustomEvent(CustomEvents.cartItemAdded, { product: item });
        emit('cart:updated');

        return { item, cart };
      });
    });
}

export function fetchCart(newestProductId) {
  return fetch(`${theme.routes.cart.base}.js`, {
    method: 'GET',
    credentials: 'include',
  })
    .then(res => res.json())
    .then(cart => {
      if (localStorageAvailable()) {
        const cartOrder = cart.items.map(e => e.id);

        // Set the initial cart order if one does not exist in storage
        if (!ls.get()) {
          ls.set(JSON.stringify(cartOrder));
        }

        if (newestProductId) {
          const indexOfNewProduct = cartOrder.indexOf(newestProductId);

          // Place newest product at the front of the cart order
          cartOrder.unshift(cartOrder.splice(indexOfNewProduct, 1)[0]);
          ls.set(JSON.stringify(cartOrder));

          // Let the cart know there is a new product at the front of the order
          hydrate({ newItemInCart: true });
        }

        cart.items = reorderCartItems(cart.items, cartOrder);
      }

      return cart;
    });
}

// Match the cart order with the order determined in local storage
function reorderCartItems(items, sortOrder) {
  return items.sort(function (a, b) {
    return sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id);
  });
}

function removeItemFromLocalOrder(id) {
  const cartOrder = ls.get();
  const indexOfProduct = cartOrder.indexOf(id);

  cartOrder.splice(indexOfProduct, 1);

  ls.set(JSON.stringify(cartOrder));
}

// See if user has local storage enabled
function localStorageAvailable() {
  var test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/*!
 * Serialize all form data into a SearchParams string
 * (c) 2020 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node}   form The form to serialize
 * @return {String}      The serialized form data
 */
function serialize(form) {
  var arr = [];
  Array.prototype.slice.call(form.elements).forEach(function (field) {
    if (
      !field.name ||
      field.disabled ||
      ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
    )
      return;
    if (field.type === 'select-multiple') {
      Array.prototype.slice.call(field.options).forEach(function (option) {
        if (!option.selected) return;
        arr.push(
          encodeURIComponent(field.name) +
            '=' +
            encodeURIComponent(option.value)
        );
      });
      return;
    }
    if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked)
      return;
    arr.push(
      encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
    );
  });
  return arr.join('&');
}

export default { addItem, addItemById, addVariant, updateItem, getCart };
